import type { MapZones } from './zoneFile';

// Maps the player loaded, kept in IndexedDB so they don't have to pick the file again, and the
// timer zones they drew for each map.
// Metadata and file bytes live in separate stores so listing maps doesn't read megabytes.
// Everything stays in this browser on this device.

export interface CachedMap {
  /** name + content hash, so two different files with the same name don't collide. */
  id: string;
  /** Map name without extension, e.g. "bhop_eazy_v2". */
  name: string;
  /** What the player picked, e.g. "bhop_eazy_v2.rar". */
  sourceName: string;
  size: number;
  addedAt: number;
  lastPlayed: number;
}

const DB_NAME = 'bhop-web';
const DB_VERSION = 3;
const META = 'maps';
const FILES = 'mapFiles';
const ZONES = 'zones';
const HANDLES = 'handles';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(ZONES)) db.createObjectStore(ZONES, { keyPath: 'map' });
        if (!db.objectStoreNames.contains(HANDLES)) db.createObjectStore(HANDLES);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('Could not open IndexedDB'));
      req.onblocked = () => reject(new Error('IndexedDB is blocked by another tab'));
    });
    dbPromise.catch(() => (dbPromise = null));
  }
  return dbPromise;
}

function done<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

export const mapId = (name: string, hash: string) => `${name.toLowerCase()}:${hash.slice(0, 16)}`;

/** Most recently played first. */
export async function listMaps(): Promise<CachedMap[]> {
  const db = await openDb();
  const all = await done(db.transaction(META).objectStore(META).getAll() as IDBRequest<CachedMap[]>);
  return all.sort((a, b) => b.lastPlayed - a.lastPlayed);
}

export async function saveMap(
  meta: Omit<CachedMap, 'addedAt' | 'lastPlayed'>,
  bsp: ArrayBuffer,
  extras: [string, Uint8Array][] = [],
): Promise<CachedMap> {
  const db = await openDb();
  const now = Date.now();
  const existing = await done(
    db.transaction(META).objectStore(META).get(meta.id) as IDBRequest<CachedMap | undefined>,
  );
  const entry: CachedMap = { ...meta, addedAt: existing?.addedAt ?? now, lastPlayed: now };
  const tx = db.transaction([META, FILES], 'readwrite');
  tx.objectStore(FILES).put({ id: meta.id, data: new Blob([bsp]), extras });
  tx.objectStore(META).put(entry);
  await txDone(tx);
  return entry;
}

export interface CachedMapFile {
  data: ArrayBuffer;
  /** Game material files (from the player's own install) this map needed. */
  extras: [string, Uint8Array][];
}

export async function loadMapFile(id: string): Promise<CachedMapFile | null> {
  const db = await openDb();
  const rec = await done(
    db.transaction(FILES).objectStore(FILES).get(id) as IDBRequest<
      { data: Blob; extras?: [string, Uint8Array][] } | undefined
    >,
  );
  return rec ? { data: await rec.data.arrayBuffer(), extras: rec.extras ?? [] } : null;
}

/** Add game material files to a cached map (merged with what it already has). */
export async function addMapExtras(id: string, extras: [string, Uint8Array][]): Promise<void> {
  if (!extras.length) return;
  const db = await openDb();
  const tx = db.transaction(FILES, 'readwrite');
  const store = tx.objectStore(FILES);
  const rec = await done(
    store.get(id) as IDBRequest<{ id: string; data: Blob; extras?: [string, Uint8Array][] } | undefined>,
  );
  if (rec) {
    const merged = new Map(rec.extras ?? []);
    for (const [k, v] of extras) merged.set(k, v);
    store.put({ ...rec, extras: [...merged] });
  }
  await txDone(tx);
}

/* ---------------- The player's game folder (Chromium keeps a handle to it) ---------------- */

export async function saveGameFolder(handle: unknown): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).put(handle, 'game');
  await txDone(tx);
}

export async function loadGameFolder<T>(): Promise<T | null> {
  const db = await openDb();
  return ((await done(db.transaction(HANDLES).objectStore(HANDLES).get('game'))) as T | undefined) ?? null;
}

export async function clearGameFolder(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).delete('game');
  await txDone(tx);
}

export async function touchMap(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(META, 'readwrite');
  const store = tx.objectStore(META);
  const m = await done(store.get(id) as IDBRequest<CachedMap | undefined>);
  if (m) store.put({ ...m, lastPlayed: Date.now() });
  await txDone(tx);
}

export async function deleteMap(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction([META, FILES], 'readwrite');
  tx.objectStore(META).delete(id);
  tx.objectStore(FILES).delete(id);
  await txDone(tx);
}

/* ---------------- Timer zones per map (keyed by map name) ---------------- */

export async function getZones(map: string): Promise<MapZones | null> {
  const db = await openDb();
  const z = await done(
    db.transaction(ZONES).objectStore(ZONES).get(map.toLowerCase()) as IDBRequest<MapZones | undefined>,
  );
  return z ?? null;
}

export async function saveZones(z: MapZones): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(ZONES, 'readwrite');
  tx.objectStore(ZONES).put({ ...z, map: z.map.toLowerCase(), updatedAt: Date.now() });
  await txDone(tx);
}

export async function deleteZones(map: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(ZONES, 'readwrite');
  tx.objectStore(ZONES).delete(map.toLowerCase());
  await txDone(tx);
}

/**
 * Game files for the CS:S weapon models, read once from the player's own install and kept here so
 * the folder isn't needed again. Stored in the key-value store next to the folder handle.
 */
export async function saveModelFiles(files: [string, Uint8Array][]): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).put(files, 'viewmodels');
  await txDone(tx);
}

export async function loadModelFiles(): Promise<[string, Uint8Array][] | null> {
  const db = await openDb();
  const v = (await done(db.transaction(HANDLES).objectStore(HANDLES).get('viewmodels'))) as
    [string, Uint8Array][] | undefined;
  return v ?? null;
}

export async function clearModelFiles(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).delete('viewmodels');
  await txDone(tx);
}

/** Weapon skin pack files the player loaded (game paths -> bytes), kept so they load next time. */
export async function saveSkinFiles(files: [string, Uint8Array][]): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).put(files, 'skins');
  await txDone(tx);
}

export async function loadSkinFiles(): Promise<[string, Uint8Array][] | null> {
  const db = await openDb();
  const v = (await done(db.transaction(HANDLES).objectStore(HANDLES).get('skins'))) as
    [string, Uint8Array][] | undefined;
  return v?.length ? v : null;
}

export async function clearSkinFiles(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(HANDLES, 'readwrite');
  tx.objectStore(HANDLES).delete('skins');
  await txDone(tx);
}
